import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@ds';
import { getRoundedInputValue, kgToLbString, lbToKgString } from '@features/forms/DonorProfileForm/utils';
import { DonorProfileFormValues } from '../types';
import { UnitLabel } from './UnitLabel';

/**
 * Weight may be entered in either kg or lb (spec: "system converts between the two").
 * `donorWeight` (kg) is the canonical RHF field; lb is local display state kept in sync —
 * whichever box the user types into becomes the source for that change.
 */
export const WeightInput = () => {
  const { control, setValue, getValues } = useFormContext<DonorProfileFormValues>();
  const [lbValue, setLbValue] = useState(() => kgToLbString(getValues('donorWeight') ?? ''));

  return (
    <Controller
      name="donorWeight"
      control={control}
      render={({ field, fieldState: { invalid, error } }) => (
        <div className="flex items-start gap-4">
          <div className="w-[148px]">
            <Input
              type="number"
              aria-label="Donor weight in kilograms"
              endContent={<UnitLabel unit="kg" />}
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
              value={getRoundedInputValue(field.value)}
              onChange={(e) => {
                field.onChange(e.target.value);
                setLbValue(kgToLbString(e.target.value));
              }}
            />
          </div>

          <span className="text-base-30 pt-2">/</span>

          <div className="w-[148px]">
            <Input
              type="number"
              aria-label="Donor weight in pounds"
              endContent={<UnitLabel unit="lb" />}
              isInvalid={invalid}
              errorMessage={error?.message}
              value={lbValue}
              onChange={(e) => {
                const value = getRoundedInputValue(e.target.value, 2);

                setLbValue(value);
                setValue('donorWeight', lbToKgString(value), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
          </div>
        </div>
      )}
    />
  );
};
